/**
 * What a task control command ACTUALLY did — the task-screen half of
 * the lift drawer's verdict idiom (`Devices/useOperatorRequest.ts`).
 *
 * WHY THIS EXISTS. `patrol.dispatch-48` was orphaned when the fleet
 * adapter pod that held it in RAM was replaced on 2026-08-28; no
 * TaskManager owned the id any more, so `cancel_task()` returned false
 * and `TaskManager::_handle_cancel_request` published NO ApiResponse at
 * all. The worker's 4 s wait therefore returned nothing, `handle_task`
 * mapped nothing to CommandState `accepted`, and the api-server
 * answered 200 OK. The screen printed the bare word "accepted" and the
 * operator read that as "cancelled" — ten times over 45 h, five
 * cancels, four kills and an interrupt, every one of which reached a
 * void. Admission is not the outcome, exactly as on the lifts.
 *
 * So: `observed_applied` is the ONLY state that means done. Everything
 * short of it is `pending` — said in words, not left to the reader to
 * infer from a lifecycle noun. `outcome_unknown` (the worker's new
 * answer for a control verb RMF never replied to) and `timed_out` are
 * verdicts of their own: not "still working", but "nobody answered".
 */
import { verdictFromError, type OperatorVerdict } from '../Devices/useOperatorRequest';
import { ApiClientError } from '../../api/v1-client';
import type { CommandRecord } from '../../api/v1';

export type { OperatorVerdict };
export { verdictFromError };

/**
 * The command is admitted but unconfirmed: published onto
 * `task_api_requests` with nothing having answered yet. Deliberately
 * NOT a verdict — the operator must not be able to read it as one.
 */
export interface TaskCommandPending {
  readonly kind: 'pending';
  readonly action: string;
  readonly message: string;
  /** The api-server's own words, when it had any. */
  readonly detail: string | null;
}

export type TaskCommandOutcome = TaskCommandPending | OperatorVerdict;

/** Human label per control verb, as the banner names it. */
export const TASK_VERB_LABEL: Readonly<Record<string, string>> = {
  cancel: 'Cancel',
  kill: 'Kill',
  interrupt: 'Interrupt',
  resume: 'Resume',
  rewind: 'Rewind',
  skip_phase: 'Skip phase',
};

/**
 * What a verdict code MEANS, in the operator's terms. Same table idiom
 * as the lift drawer's `VERDICT_CODE_TEXT`; the api-server's `reason`
 * is kept underneath for ops rather than replaced.
 *
 * Hotel words (ui-copy-fix-proposals P8.8, 2026-10-31): the front desk
 * reads these, and they said "fleet adapter" and "RMF". TIMED_OUT also
 * says to check before pressing again: the command may have been acted
 * on, and a second cancel or kill is not always harmless.
 */
export const TASK_VERDICT_CODE_TEXT: Readonly<Record<string, string>> = {
  OUTCOME_UNKNOWN:
    'Nothing is in charge of this task any more, so pressing again will not help. ' +
    'The system closes it by itself. Do not press again.',
  TIMED_OUT:
    'No answer came back in time. The robot may or may not have acted on it. Check the ' +
    "task's status; if nothing has changed after a minute, press again.",
  // A next step, now the server's own reason is ops' only (review BC-F8,
  // 2026-10-31): the front desk read "The system refused it." and nothing
  // after it. Verb-neutral, as the pending line is: this answers Resume
  // as well as Cancel.
  REJECTED: "The system refused it. Check the task's status before you press again; if it keeps refusing, call Nexsoss.",
};

/**
 * The banner's status word for a site reader (review BC-F8): the heading
 * read "Cancel: timeout (OUTCOME_UNKNOWN)" or "Kill: refused (REJECTED)"
 * to the front desk, a status enum and a wire code. Ops keep both. "done"
 * stays the word it was: the help says only "done" means it stopped.
 */
export const SITE_VERDICT_STATUS_WORD: Readonly<Record<string, string>> = {
  done: 'done',
  refused: 'refused',
  timeout: 'not answered',
  failed: 'not sent',
};

/**
 * What a site reader reads under "<verb>: done" (final gate, 2026-11-01).
 * The worker answers every applied task command with its own words
 * ("confirmed by rmf task api"), so `taskCommandOutcome`'s "Confirmed."
 * fallback was never reached and the front desk read the worker's line
 * after every Cancel, Kill or Resume that worked. Those words are ops'
 * only, as every other verdict's are (P8.8); the phone's Cancel already
 * says "Cancelled." here.
 */
export const SITE_DONE_TEXT = 'Confirmed.';

function labelFor(action: string): string {
  return TASK_VERB_LABEL[action] ?? action;
}

/**
 * Map a `CommandRecord` onto the outcome the screen shows.
 *
 * `received`/`authorized`/`published`/`accepted` all mean the same
 * thing to the person watching — the request left the building and
 * nothing has come back — so they collapse into one honest `pending`.
 */
export function taskCommandOutcome(action: string, command: CommandRecord): TaskCommandOutcome {
  const ts = Date.now();
  const verb = labelFor(action);
  const detail = command.outcome_message ?? null;

  if (command.state === 'observed_applied') {
    return {
      kind: 'verdict',
      verb,
      request_id: command.correlation?.rmf_request_id ?? null,
      status: 'done',
      code: null,
      reason: detail ?? 'Confirmed.',
      wait_name: null,
      phase: null,
      ts,
      preflight: false,
    };
  }

  if (command.state === 'rejected') {
    return {
      kind: 'verdict',
      verb,
      request_id: command.correlation?.rmf_request_id ?? null,
      status: 'refused',
      code: 'REJECTED',
      reason: detail ?? 'The system refused it.',
      wait_name: null,
      phase: null,
      ts,
      preflight: false,
    };
  }

  if (command.state === 'timed_out' || command.state === 'outcome_unknown') {
    return {
      kind: 'verdict',
      verb,
      request_id: command.correlation?.rmf_request_id ?? null,
      status: 'timeout',
      code: command.state === 'outcome_unknown' ? 'OUTCOME_UNKNOWN' : 'TIMED_OUT',
      reason: detail ?? 'No answer came back.',
      wait_name: null,
      phase: null,
      ts,
      preflight: false,
    };
  }

  return {
    kind: 'pending',
    action,
    // Verb-neutral: "not stopped" was wrong for Resume.
    message: `${verb} sent. Nothing has changed until the task's status does.`,
    detail,
  };
}

/**
 * The three worker outcomes the api-server answers with a 409, all of
 * which it labels with the SAME error code.
 *
 * `commands.py::_authorize_and_publish` raises
 *
 *   HTTPException(409, detail={
 *     "code": "precondition_failed",
 *     "message": record.outcome_message or "command not accepted",
 *     "detail": {"command": record.model_dump(mode="json")},
 *   })
 *
 * for `rejected`, `timed_out` AND `outcome_unknown` alike, and FastAPI
 * wraps that in its own `{"detail": ...}` — so on the wire the body is
 * `{detail: {code, message, detail: {command: CommandRecord}}}` and
 * `ApiClientError` files the middle object as `.detail`. The record at
 * `detail.detail.command` is therefore the ONLY thing that says which
 * of the three actually happened; the code says only that one of them
 * did.
 */
const REFUSED_COMMAND_STATES: ReadonlySet<string> = new Set([
  'rejected',
  'timed_out',
  'outcome_unknown',
]);

function asRecord(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined;
}

/**
 * The `CommandRecord` a 409 carried, when it carried one.
 *
 * WHY. Every branch of `taskCommandOutcome` below `observed_applied`
 * used to be unreachable in production: the worker's real answer
 * (`outcome_unknown` — nobody owns this task, no cancel will ever
 * work) reaches the SPA only as a 409 whose code is the generic
 * `precondition_failed`, so the banner read "Cancel: refused
 * (precondition_failed)" and the one sentence written to stop an
 * operator re-sending a doomed cancel — the behaviour that made the
 * 2026-08-28 incident last 45 h — never rendered.
 *
 * `precondition_failed` is deliberately NOT mapped to a state here.
 * The api-server emits it for a genuine `rejected` too, and telling an
 * operator "do not re-send, the task is orphaned" about a command RMF
 * simply refused would be a new lie in place of the old one. Only the
 * record's own `state` decides, and only these three states are read:
 * an `accepted`/`observed_applied` record inside an error envelope is
 * a contradiction, and rendering it would turn a refusal into a
 * "pending" or, worse, a green "done".
 *
 * Returns null for anything else — a non-409, a transport error, or an
 * api-server old enough to send no `command` in the body at all. The
 * caller then falls back to `verdictFromError`, which is what every
 * refusal used before this and is still an honest refusal.
 */
export function refusedCommandFromError(err: unknown): CommandRecord | null {
  if (!(err instanceof ApiClientError) || err.status !== 409) return null;
  const command = asRecord(asRecord(err.detail?.detail)?.command);
  if (!command) return null;
  if (typeof command.state !== 'string' || !REFUSED_COMMAND_STATES.has(command.state)) return null;
  return command as unknown as CommandRecord;
}
